/**
 * Product card — Showroom style, DYNAMIC schema: renders the sheet's own
 * columns (product.attrs, already in admin-configured order). No prices —
 * they are never synced. Legacy named fields remain as a fallback for old
 * data (local emulator seeds).
 */
import { memo } from 'react';
import type { Product } from '@mitang/shared';
import { formatPriceCell } from '../../lib/format';
import { HeartButton } from '../../components/HeartButton';

interface Props {
  product: Product;
  recentlyUpdated: boolean;
  onOpen: (product: Product) => void;
  /** Price columns — present only for admin-allow-listed users. */
  priceAttrs?: Array<{ k: string; v: string }>;
  saved: boolean;
  onToggleSaved: (product: Product) => void;
}

/** Urgent remarks (limited stock) go red; everything else soft amber. */
function remarkTone(remarks: string): string {
  return /last|few|left|only/i.test(remarks)
    ? 'bg-hot text-white'
    : 'bg-amber-50 text-stale';
}

/** Chip values: sheet columns minus the ones already shown (title, part no). */
export function cardChips(product: Product): string[] {
  if (product.attrs && product.attrs.length > 0) {
    return product.attrs
      .map((a) => a.v)
      .filter((v) => v !== product.modelNumber && v !== product.partNo)
      .slice(0, 4);
  }
  return [product.processor, product.ram, product.storage, product.graphics ?? product.display]
    .filter((v): v is string => !!v)
    .slice(0, 4);
}

export const ProductCard = memo(function ProductCard({
  product,
  recentlyUpdated,
  onOpen,
  priceAttrs,
  saved,
  onToggleSaved,
}: Props): JSX.Element {
  const chips = cardChips(product);
  const price = priceAttrs?.[0]?.v;

  return (
    <article
      className="relative rounded-[18px] bg-white p-4 pb-3.5 shadow-card transition-transform active:scale-[.99]"
      onClick={() => onOpen(product)}
    >
      {/* Absolutely positioned so adding it cannot change the card's height —
          ProductList is virtualized with fixed row heights. Sits level with the
          model number, not the badge row, so it reads as part of the card. */}
      <div className="absolute right-4 top-[30px]">
        <HeartButton saved={saved} onToggle={() => onToggleSaved(product)} />
      </div>

      <div className="flex items-center gap-2 pr-8">
        {product.series ? (
          <span className="text-[10px] font-bold uppercase tracking-[.1em] text-slate-400">
            {product.series}
          </span>
        ) : null}
        {product.remarks ? (
          <span
            className={`rounded-full px-2 py-[3px] text-[9.5px] font-extrabold uppercase tracking-[.06em] ${remarkTone(product.remarks)}`}
          >
            {product.remarks}
          </span>
        ) : null}
        <span className="ml-auto text-[10px] font-semibold text-drop">
          {recentlyUpdated ? '● Updated just now' : ''}
        </span>
      </div>

      <h3 className="mt-0.5 truncate text-[15.5px] font-bold tracking-tight text-ink">
        {product.modelNumber}
      </h3>
      {product.partNo && product.partNo !== product.modelNumber ? (
        <p className="font-mono text-[11px] text-slate-400">{product.partNo}</p>
      ) : null}

      <div className="mt-2 flex h-[56px] flex-wrap content-start gap-1.5 overflow-hidden">
        {chips.map((c) => (
          <span
            key={c}
            className="whitespace-nowrap rounded-full bg-chipbg px-2.5 py-1 text-[10.5px] font-medium text-slate-600"
          >
            {c.length > 34 ? `${c.slice(0, 33)}…` : c}
          </span>
        ))}
      </div>

      {price ? (
        <div className="mt-1 text-right text-[22px] font-extrabold tracking-tight text-ink-price [font-variant-numeric:tabular-nums]">
          {formatPriceCell(price)}
        </div>
      ) : null}
    </article>
  );
});
